import clsx from 'clsx';
import { CountryCode, isValidPhoneNumber } from 'libphonenumber-js';
import React, { useEffect } from 'react';
import IntlTelInputBase from 'react-intl-tel-input';
import { twMerge } from 'tailwind-merge';

export interface IntlTelData {
  phone: string;
  phoneCountryCode: string;
  phonePrefix: string;
  isPhoneValid: boolean;
  countryName?: string;
}

interface Props {
  phone: string;
  phoneCountryCode: string;
  onChange?: (data: IntlTelData) => void;
  focusBorderColor?: string;
  disabled?: boolean;
  fieldName?: string;
  onFocus?: () => void;
  onBlur?: () => void;
  id?: string;
  transparentBg?: boolean;
  status?: '' | 'error' | 'warning';
  inputClassName?: string;
  placeholder?: string;
}

export const IntlTelInput: React.FC<Props> = ({
  phone,
  phoneCountryCode,
  onChange,
  focusBorderColor = 'focus:border-bgDarkGray',
  fieldName = '',
  disabled,
  onBlur,
  id = 'phone',
  transparentBg = false,
  status,
  inputClassName,
  placeholder,
}) => {
  const inputClasses = [
    'font-normal',
    'border',
    'border-solid',
    'border-bgDarkGray',
    'py-2',
    'leading-tight',
    'px-3.5',
    'outline-0',
    'focus-visible:ring-0',
    focusBorderColor,
  ];

  const checkNumberValidity = (number: unknown, iso2: string) => {
    if (typeof number !== 'string' && typeof number !== 'number') return false;

    const numStr = String(number);
    if (!numStr || typeof numStr.charAt !== 'function') return false;

    return (
      iso2 &&
      isValidPhoneNumber(numStr, iso2.toUpperCase() as CountryCode) &&
      numStr.charAt(0) !== '0'
    );
  };

  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined')
      return;

    const targetPlaceholder = placeholder || 'Enter phone number';
    const interval = setInterval(() => {
      const input = (
        id
          ? document.querySelector(`#${id} .intl-tel-input input`)
          : document.querySelector('.intl-tel-input input')
      ) as HTMLInputElement | null;

      if (input && input.placeholder !== targetPlaceholder) {
        input.placeholder = targetPlaceholder;
        clearInterval(interval);
      }
    }, 100);

    return () => clearInterval(interval);
  }, [placeholder, id]);

  return (
    <div
      id={id}
      className='intl-tel-wrapper'
      onBlurCapture={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) {
          onBlur?.();
        }
      }}
    >
      <IntlTelInputBase
        disabled={disabled}
        placeholder={placeholder || 'Enter phone number'}
        inputClassName={twMerge(
          clsx(
            ...inputClasses,
            'pl-[100px] w-full',
            disabled
              ? 'text-gray-400 cursor-not-allowed'
              : 'text-normal border text-sm focus-within:bg-bgColor placeholder:text-textDarkGrayColor rounded-lg border-bgDarkGray py-3 text-white',
            transparentBg
              ? 'bg-transparent'
              : 'bg-bgColor customInputBlack makeBgBlack',
            status === 'error' && 'border-red-500 focus:border-red-500',
            status === 'warning' && 'border-yellow-500',
            inputClassName,
          ),
        )}
        fieldName={fieldName}
        preferredCountries={['in', 'us', 'ae']}
        value={phone ?? ''}
        separateDialCode={true}
        defaultCountry={phoneCountryCode?.toLowerCase() || 'in'}
        formatOnInit={false}
        format={false}
        onSelectFlag={(number, { iso2, dialCode, name }, _, isValid) => {
          const valid = isValid ?? checkNumberValidity(number, iso2 as string);
          onChange?.({
            phone: String(number),
            phoneCountryCode: iso2 as string,
            phonePrefix: dialCode as string,
            isPhoneValid: valid,
            countryName: name,
          });
        }}
        onPhoneNumberChange={(isValid, value, { dialCode, iso2, name }) => {
          const valid = isValid ?? checkNumberValidity(value, iso2 as string);
          onChange?.({
            phone: String(value),
            phoneCountryCode: iso2 as string,
            phonePrefix: dialCode as string,
            isPhoneValid: valid,
            countryName: name,
          });
        }}
      />
    </div>
  );
};
